import React, { type ChangeEvent, type MouseEvent, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { FormLabel } from '@mui/material'
import { toast } from 'react-toastify'
import { useLocation } from 'react-router-dom'
import {
  CustomButtonGroup,
  CustomInput,
  DashboardLoader,
  RadioButton
} from '../../../globalUtils/globalExports'
import { checkValidForFTPString, keyPressValidationForFTPString } from '../../../globalUtils/globalHooks'
import hwAxios from '../../../rmsReduxStore/api-service/HWAxios'
import appUser from '../../../auth/service/AppUser'
import ChargerPanelSubNavListHeader from './ChargerPanelSubNavListHeader'
import './distributedChargerPanelRemoteCommands.css'

type DistributedTarget = 'powerCube' | 'dispenser'

export interface IRemoteCommand { buttonText: string, buttonIconClassName: string, buttonId: string }
interface IConnectorOptions { radioButtonId: string, radioButtonLabel: string }
export interface IDistributedDispenser { dispenserNumber: number, connectors: IConnectorOptions[] }

interface DistributedChargerPanelRemoteCommandsProps {
  contentHeight: number
  powerCubeCommands: IRemoteCommand[]
  dispenserCommands: IRemoteCommand[]
  dispensers: IDistributedDispenser[]
}

const targetTabs: { id: DistributedTarget, text: string }[] = [
  { id: 'powerCube', text: 'Power Cube' },
  { id: 'dispenser', text: 'Dispenser' },
]

const ftpUrlCommands = ['ftp_software_upgrade', 'ftp_log_upload', 'ftp_certificate_upgrade', 'ftp_screen_saver_images_upgrade', 'ftp_customer_logo_update',
  'ftp_config_file_update', 'ftp_config_file_upgrade', 'upload_ssl_certificates_to_ftp', 'upload_config_files_to_ftp', 'upload_logs_to_ftp', 'ftp_ssl_certificate_upgrade']
const bothConnectorsCommands = ['plc_software_upgrade', 'plc_reset']
const connectorCommands = ['disable_connector', 'enable_connector', ...bothConnectorsCommands]
const BOTH_CONNECTORS_ID = 'BOTH_CONNECTORS'

function DistributedChargerPanelRemoteCommands({ contentHeight, powerCubeCommands, dispenserCommands, dispensers }: DistributedChargerPanelRemoteCommandsProps) {
  const location = useLocation()

  const [selectedTarget, setSelectedTarget] = useState<DistributedTarget>('powerCube')
  const [selectedCommand, setSelectedCommand] = useState<string | null>(null)
  const [selectedModule, setSelectedModule] = useState<number>(0)
  const [selectedConnectorValue, setSelectedConnectorValue] = useState<string | null>(null)
  const [urlValue, setUrlValue] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState('')
  const [showLoader, setShowLoader] = useState(false)

  const commands = useMemo(() => {
    return (selectedTarget === 'powerCube' ? powerCubeCommands : dispenserCommands) ?? []
  }, [selectedTarget, powerCubeCommands, dispenserCommands])

  const selectedDispenser = useMemo(() => dispensers?.find((dispenser) => dispenser?.dispenserNumber === selectedModule), [dispensers, selectedModule])

  const isDispenserRequired = selectedTarget === 'dispenser'
  const isCommandOptionsDisabled = isDispenserRequired && !selectedDispenser
  const isFtpUrlRequired = useMemo(() => !!selectedCommand && ftpUrlCommands.includes(selectedCommand), [selectedCommand])
  const isConnectorRequired = useMemo(() => isDispenserRequired && !!selectedCommand && connectorCommands.includes(selectedCommand), [isDispenserRequired, selectedCommand])
  const isBothConnectorsRequired = useMemo(() => isConnectorRequired && !!selectedCommand && bothConnectorsCommands.includes(selectedCommand), [isConnectorRequired, selectedCommand])
  const isBothConnectorsSelected = isBothConnectorsRequired && selectedConnectorValue === BOTH_CONNECTORS_ID
  const connectorOptions = useMemo<IConnectorOptions[]>(() => {
    const dispenserConnectors = selectedDispenser?.connectors ?? []
    if (isBothConnectorsRequired && dispenserConnectors?.length > 1) {
      return [
        ...dispenserConnectors,
        { radioButtonId: BOTH_CONNECTORS_ID, radioButtonLabel: `Both Connectors (${dispenserConnectors.map((connector) => connector.radioButtonLabel).join(' & ')})` }
      ]
    }
    return dispenserConnectors
  }, [selectedDispenser, isBothConnectorsRequired])

  const selectedConnectorId = isConnectorRequired && !isBothConnectorsSelected ? selectedConnectorValue : null


  useEffect(() => {
    setSelectedCommand(commands?.length > 0 ? commands[0].buttonId : null)
  }, [commands])

  useEffect(() => {
    setSelectedConnectorValue((prevValue) => connectorOptions?.some((connector) => connector?.radioButtonId === prevValue) ? prevValue : (connectorOptions?.[0]?.radioButtonId ?? null))
  }, [connectorOptions])

  // Default to the first dispenser, and fall back to it if the selected one is no longer in the list
  useEffect(() => {
    if (isDispenserRequired && !selectedDispenser) {
      setSelectedModule(dispensers?.[0]?.dispenserNumber ?? 0)
    }
  }, [isDispenserRequired, selectedDispenser, dispensers])

  useEffect(() => {
    setUrlValue('')
    setErrorMessage('')
  }, [selectedCommand, selectedTarget])

  const handleTargetChange = useCallback((target: DistributedTarget) => {
    setSelectedTarget(target)
    setSelectedModule(0)
  }, [])

  const handleNavBtnClick = useCallback((event: MouseEvent<HTMLButtonElement>, val: string) => {
    setSelectedCommand(val)
  }, [])

  const handleConnectorChange = useCallback((event: ChangeEvent<HTMLInputElement>, value: string) => {
    setSelectedConnectorValue(value)
  }, [])

  const handleUrlChange = useCallback((event) => {
    const validValue = checkValidForFTPString(event?.target?.value)
    setErrorMessage(validValue ? '' : 'Please enter valid value.')
    setUrlValue(validValue)
  }, [])

  const onPerformBtnClick = useCallback(() => {
    if (isDispenserRequired && !selectedModule) {
      toast.error('Please select the dispenser.')
      return
    }
    if (isConnectorRequired && !selectedConnectorValue) {
      toast.error('Please select the connector.')
      return
    }
    if (isFtpUrlRequired && !urlValue?.trim()) {
      toast.error('Please enter the URL.')
      return
    }

    const body = {
      remoteCommand: selectedCommand,
      userId: appUser.user?.id,
      chargerId: location?.state?.chargerId,
      connectorId: selectedConnectorId,
      module: isDispenserRequired ? selectedModule : 0,
    }
    if (isBothConnectorsSelected) { body['bothConnectors'] = true }
    if (urlValue) { body['ftpUrl'] = urlValue }

    setShowLoader(true)
    hwAxios.post(process.env.REACT_APP_SERVER_BASE_URL + '/charger/remote-command', body).then((response) => {
      setShowLoader(false)
      const message = response?.data?.message
      if (response?.data?.statusCode == 200) {
        toast.success(message)
      } else {
        toast.error(message)
      }
    }).catch((error) => {
      console.log('Error Message', JSON.stringify(error))
      setShowLoader(false)
      toast.error('Something went wrong. Please try again.')
    })
  }, [selectedCommand, selectedModule, selectedConnectorValue, selectedConnectorId, urlValue, isDispenserRequired, isConnectorRequired, isBothConnectorsSelected, isFtpUrlRequired,
    location])

  const btnsList = useMemo(() => {
    return [
      {
        buttonText: 'Perform',
        buttonId: 'perform',
        btnClassName: 'primary__btn',
        handleClick: onPerformBtnClick,
        isDisabled: !selectedCommand,
        buttonVariant: 'filled'
      }
    ]
  }, [onPerformBtnClick, selectedCommand])

  return (
    <div className='distributed__remote__commands' style={{ height: `${contentHeight - 10}px` }}>
      <DashboardLoader handleClose={setShowLoader} showLoader={showLoader} />

      <div className='distributed__remote__commands-target-tabs'>
        {targetTabs?.map((tab) => (
          <div
            key={'distributed-target-' + tab.id}
            id={'distributed-target-' + tab.id}
            className={selectedTarget === tab.id ? 'distributed__remote__commands-target-tab selected' : 'distributed__remote__commands-target-tab'}
            onClick={() => handleTargetChange(tab.id)}
          >
            {tab.text}
          </div>
        ))}
      </div>

      {commands?.length > 0 && selectedCommand ?
        <>
          <div className='distributed__remote__commands-section'>
            <ChargerPanelSubNavListHeader handleNavBtnClick={handleNavBtnClick} selectedNav={selectedCommand} navList={commands} dashboardNavRef={null} />
          </div>

          {(isDispenserRequired || isFtpUrlRequired) &&
            <div className='distributed__remote__commands-section distributed__remote__commands-form'>
              {isDispenserRequired &&
                <div className='distributed__remote__commands-form-row'>
                  <div className='distributed__remote__commands-form-label'>Dispenser ID:</div>
                  <div className='distributed__remote__commands-dispenser-btns'>
                    {!(dispensers?.length > 0) && <span className='distributed__remote__commands-empty-text'>No dispensers available.</span>}
                    {dispensers?.map((dispenser) => (
                      <button
                        type='button'
                        key={'dispenser-' + dispenser.dispenserNumber}
                        title={''+dispenser.dispenserNumber}
                        className={selectedModule === dispenser?.dispenserNumber ? 'distributed__remote__commands-dispenser-btn selected' : 'distributed__remote__commands-dispenser-btn'}
                        onClick={() => setSelectedModule(dispenser?.dispenserNumber)}
                      >
                        {dispenser.dispenserNumber}
                      </button>
                    ))}
                  </div>
                </div>
              }

              {isConnectorRequired && connectorOptions?.length > 0 &&
                <div className='distributed__remote__commands-form-row'>
                  <div className='distributed__remote__commands-form-label'>Connector:</div>
                  <RadioButton
                    key={selectedModule + (selectedConnectorValue ?? 'distributed-connector')}
                    buttonsList={connectorOptions?.map((connector) => ({ ...connector, isDisabled: isCommandOptionsDisabled }))}
                    handleSelectedRadioBtnChange={handleConnectorChange}
                    selectedRadioBtnValue={selectedConnectorValue}
                    radionBtnGrpName={'distributed-charger-remote-commands-connector-radio-buttons'}
                  />
                </div>
              }

              {isFtpUrlRequired &&
                <div className='distributed__remote__commands-url-wrap'>
                  <FormLabel className={'rms-form-label'} required>
                    URL
                  </FormLabel>
                  <CustomInput
                    inputPlaceHolder='Type here'
                    handleInputChange={handleUrlChange}
                    inputValue={urlValue || ''}
                    inputFieldId='distributed_remote_command_url'
                    isRequired={true}
                    handleKeyPress={keyPressValidationForFTPString}
                    errorMessage={errorMessage}
                    isDisabled={isCommandOptionsDisabled}
                  />
                </div>
              }
            </div>
          }

          <div className='distributed__remote__commands-section distributed__remote__commands-footer'>
            <CustomButtonGroup buttonsList={btnsList} buttonGroupClassName={'button__group__footer'} />
          </div>


        </> :
        <div className='distributed__remote__commands-empty'>No remote commands available.</div>
      }
    </div>
  )
}

export default memo(DistributedChargerPanelRemoteCommands)
